import locationApi from "./locationApi"

/* Toutes ces fonctions laissent remonter l'erreur axios : renvoyer l'erreur
   comme une valeur faisait passer un refus serveur (400, 403, 500) pour un
   succès chez les appelants (Z2-4). L'appelant affiche le message. */

export async function getToken() {
  const response = await locationApi.get('/location/data-collected')
  return response.data
}

export async function getActiveLocation() {
  const response = await locationApi.get('/location/active/getall')
  return response.data
}

export async function getAllLocations() {
  const response = await locationApi.get('/location/getall')
  return response.data
}

/* Page Lieux (Z2-12) : la file est paginée par le serveur, la carte ne
   demande que la zone visible. */
export type OngletLieux = 'attente' | 'verifie' | 'approuve' | 'rejete' | 'expire' | 'masque'

export interface PageLieux {
  contenu: any[]
  total: number
  page: number
  taille: number
  compteurs: Record<OngletLieux, number>
}

export async function getLieuxPage(params: {
  onglet: OngletLieux; recherche?: string; verdict?: string; triDouteux?: boolean; page: number; taille: number
}): Promise<PageLieux> {
  const response = await locationApi.get('/location/backoffice/lieux', { params })
  return response.data
}

export interface CarteLieux {
  points: any[]
  total: number
  tronque: boolean
  plafond: number
}

export async function getCarteLieux(params: {
  minLat: number; maxLat: number; minLng: number; maxLng: number; zoom: number
}): Promise<CarteLieux> {
  const response = await locationApi.get('/location/backoffice/carte', { params })
  return response.data
}

export async function getById(referenceid: any) {
  const response = await locationApi.get('/location/getbyid/' + referenceid)
  return response.data
}

export async function getByPlacekey(placekey: any) {
  const response = await locationApi.get('/location/getbyid/placekey/' + placekey)
  return response.data
}

export async function updateLocation(formData: any) {
  const res = await locationApi.put(`/location/update`, formData);
  return res.data;
}

export async function updateLocationByPlacekey(formData: any) {
  const res = await locationApi.put(`/location/update-by-placekey`, formData);
  return res.data;
}

// Vague 1 : référentiel des motifs de rejet (liste contrôlée).
export async function getMotifsRejet() {
  const res = await locationApi.get(`/location/motifs-rejet`);
  return res.data;
}

// Vague Vision : lancer l'analyse photo à la demande (synchrone).
export async function analyserVision(objectType: string, ids: string[]) {
  const res = await locationApi.post(`/location/vision/analyser`, { objectType, ids });
  return res.data;
}

// Vague 1 : historique des décisions d'un objet (POI ou soumission).
export async function getDecisions(objectType: string, objectId: string) {
  const res = await locationApi.get(`/location/decisions`, { params: { objectType, objectId } });
  return res.data;
}

/* Masquer un POI exige un motif, conservé au journal des décisions.
   L'erreur remonte à l'appelant (motif manquant, droits, POI introuvable). */
export async function updateDeleteLocation(formData: any, motif?: string) {
  const res = await locationApi.put(`/location/delete`, formData, { params: motif ? { motif } : {} });
  return res.data;
}

/**
 * Toutes les quêtes, page par page : GET /quest/all renvoie une Page Spring
 * ({ content, last }), pas un tableau. Les pages qui lisaient un tableau
 * affichaient 0 (Z1-7). rootsOnly=false inclut les cycles récurrents, qui
 * portent leurs propres collectes.
 */
export async function toutesLesQuetes(rootsOnly = false): Promise<any[]> {
  const toutes: any[] = [];
  for (let page = 0; page < 100; page++) {
    const r = await locationApi.get('/quest/all', { params: { page, size: 200, rootsOnly } });
    const contenu = Array.isArray(r.data?.content) ? r.data.content : [];
    toutes.push(...contenu);
    if (r.data?.last !== false || contenu.length === 0) break;
  }
  return toutes;
}

/* ── Niveaux (décision D7) : référentiel unique, servi par location-service.
   Le job de nuit n'accorde jamais un niveau inactif ; le niveau 1 reste actif. */
export interface Niveau {
  niveau: number
  nom: string
  avantages: string | null
  actif: boolean
  minApprouves: number | null
  tauxApprobationMin: number | null
  ancienneteJoursMin: number | null
  categoriesMin: number | null
}

export async function getNiveaux(): Promise<Niveau[]> {
  const res = await locationApi.get('/niveaux');
  return Array.isArray(res.data)
    ? [...res.data].sort((a: Niveau, b: Niveau) => a.niveau - b.niveau)
    : [];
}

/** Motif obligatoire (400 sinon) ; l'erreur remonte avec le texte du serveur. */
export async function majNiveau(niveau: number, corps: Partial<Niveau> & { motif: string }) {
  const res = await locationApi.put(`/niveaux/${niveau}`, corps);
  return res.data;
}

/* ── Cycle de vie d'une quête. Les refus du serveur (400, 409) remontent à
   l'appelant, qui affiche leur texte (messageServeur). */

/** Unique action de publication (Q8) : contrôles, e-mail client, push Spotters, journal. */
export async function publierQuete(id: string) {
  const res = await locationApi.put(`/quest/${id}/publish`);
  return res.data;
}

/** Fin de vie (Q9) : la quête passe au statut « archivee ». */
export async function cloturerQuete(id: string) {
  const res = await locationApi.put(`/quest/${id}/cloturer`);
  return res.data;
}

/** Refusée (400) si la quête a des soumissions : il faut la clôturer. */
export async function supprimerQuete(id: string) {
  const res = await locationApi.delete(`/quest/${id}`);
  return res.data;
}

/** Relance d'un crédit de Tips en échec (admin) : location-service rejoue la
    même clé auprès de user-service, jamais de double crédit. 200 crédité,
    502 nouvel échec, 409 aucun crédit en échec. */
export async function relancerCredit(type: "poi" | "soumission" | "challenge", id: string) {
  const res = await locationApi.post(`/credit/relancer/${type}/${encodeURIComponent(id)}`);
  return res.data;
}

/** Désigne le gagnant d'un challenge (D12) : soumission validée, une seule fois (409 ensuite). */
export async function designerGagnant(questId: string, submissionId: string) {
  const res = await locationApi.put(`/quest/${questId}/winner`, { submissionId });
  return res.data;
}

/** CSV des soumissions d'une quête (GET /api/export/{questId}, admin ou propriétaire). */
export async function exporterSoumissionsCsv(questId: string): Promise<Blob> {
  const res = await locationApi.get(`/api/export/${questId}`, { responseType: 'blob' });
  return res.data;
}

/* ── Push et agrégats d'administration (pages Communications, Export) ──
   L'erreur remonte : un refus (400 cible incomplète, 403) n'est jamais un succès. */

/** Envoi immédiat ; le serveur répond { success, destinataires } ou 400 { erreur }. */
export async function envoyerPush(payload: any) {
  const res = await locationApi.post('/notification/send', payload);
  return res.data;
}

/** 20 derniers envois enregistrés par le serveur. */
export async function getHistoriquePush(): Promise<any[]> {
  const res = await locationApi.get('/notification/history');
  return Array.isArray(res.data) ? res.data : [];
}

/** Agrégat par Spotter : email, total, approuves, rejetes, enAttente, premier, dernier, ville. */
export async function getStatistiquesSpotters(): Promise<any[]> {
  const res = await locationApi.get('/location/statistiques/spotters');
  return Array.isArray(res.data) ? res.data : [];
}

/** Liste légère des POI non supprimés (sans images ni réponses). */
export async function getPositions(): Promise<any[]> {
  const res = await locationApi.get('/location/positions');
  return Array.isArray(res.data) ? res.data : [];
}

// Vague 2 : synthèse d'intégrité (rejets et signaux par règle sur N jours).
// null en cas d'échec : la page Configuration affiche alors la section sans synthèse.
export async function getSyntheseIntegrite(jours: number) {
  try {
    const res = await locationApi.get('/integrite/synthese', { params: { jours } })
    return res.data
  } catch (e) { console.log(e); return null }
}

// Vague 2, lot 4 : comptes testeurs (position virtuelle, captures marquées TEST).
// Les erreurs remontent : l'appelant affiche le message du serveur.
export async function getTesteurs(): Promise<any[]> {
  const res = await locationApi.get('/testeur')
  return Array.isArray(res.data) ? res.data : []
}
export async function enregistrerTesteur(email: string, ville: string, actif: boolean) {
  const res = await locationApi.put('/testeur', { email, ville, actif })
  return res.data
}
export async function supprimerTesteur(email: string) {
  const res = await locationApi.delete(`/testeur/${encodeURIComponent(email)}`)
  return res.data
}
